import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ISavedFilter,
  ISavedFilterSchedule,
  isSchedulableSavedFilterType,
} from 'shared';

import { Modal } from '../../../common/Atoms/Modal';
import { Button } from '../../../common/Atoms/Button';
import { Notification } from '../../../common/Atoms/Notification';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { useDeleteSavedFilterMutation, useUpdateSavedFilterMutation } from '../../services/api/savedFiltersApi/savedFilters';

import { SavedFilterScheduleFields } from './SavedFilterScheduleFields';

interface SavedFilterSettingsModalProps {
  filter: ISavedFilter | null;
  organisationId: string;
  onClose: () => void;
}

// Settings for one saved filter: schedule an emailed export of it and delete it (with confirmation)
export function SavedFilterSettingsModal({ filter, organisationId, onClose }: SavedFilterSettingsModalProps) {
  const [updateSavedFilter, { isLoading: saving }] = useUpdateSavedFilterMutation();
  const [deleteSavedFilter, { isLoading: deleting }] = useDeleteSavedFilterMutation();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draft, setDraft] = useState<ISavedFilterSchedule | null>(null);
  // Keyed on the filter so reopening the modal for another filter resets the draft
  const [draftFor, setDraftFor] = useState<string | null>(null);

  const schedulable = !!filter && isSchedulableSavedFilterType(filter.type);

  const schedule = useMemo(() => {
    if (!filter) return null;

    return draftFor === filter.id ? draft : filter.schedule;
  }, [filter, draft, draftFor]);

  function setSchedule(next: ISavedFilterSchedule | null) {
    setDraft(next);
    setDraftFor(filter?.id ?? null);
  }

  async function save(next: ISavedFilterSchedule | null) {
    if (!filter) return;

    if (next && next.userIds.length === 0) {
      toast.error(`Choose at least one recipient`);

      return;
    }

    try {
      await updateSavedFilter({ organisationId, id: filter.id, schedule: next }).unwrap();

      toast.success(next ? `Scheduled emails saved` : `Scheduled emails turned off`);
      handleClose();
    }
    catch (err) {
      toast.error(err?.data?.toString() || `Could not save settings`);
    }
  }

  async function onDelete() {
    if (!filter) return;

    try {
      await deleteSavedFilter({ organisationId, id: filter.id }).unwrap();

      toast.success(`Deleted "${filter.name}"`);
      onClose();
    }
    catch (err) {
      toast.error(`Could not delete saved filter`);
    }
  }

  function handleClose() {
    setConfirmDelete(false);
    setDraftFor(null);
    onClose();
  }

  return (
    <Modal
      title={ filter ? `"${filter.name}" settings` : `` }
      open={ !!filter }
      setOpen={ open => !open && handleClose() }
    >
      <div className={ `space-y-4 min-w-[350px]` }>
        { schedulable && !filter?.hasExportParams && (
          <Notification type={ `info` }>
            { `Open this view and save it again to enable scheduled emails.` }
          </Notification>
        ) }

        { schedulable && (
          <SavedFilterScheduleFields
            schedule={ schedule }
            onChange={ setSchedule }
            disabled={ !filter?.hasExportParams }
          />
        ) }

        { filter?.schedule && (
          <button
            type={ `button` }
            onClick={ () => save(null) }
            disabled={ saving }
            className={ `text-sm text-red-600 hover:underline` }
          >
            { `Turn off scheduled emails` }
          </button>
        ) }

        { confirmDelete ? (
          <Notification type={ `warning` }
            title={ `Delete "${filter?.name}"?` }
            buttons={
              <div className={ `flex gap-2` }>
                <Button color={ `gray` }
                  onClick={ () => setConfirmDelete(false) }>
                  { `Keep it` }
                </Button>
                <Button color={ `red` }
                  onClick={ onDelete }
                  loading={ deleting }>
                  { `Delete` }
                </Button>
              </div>
            }
          >
            <Paragraph variant={ `secondary` }>{ `This removes the saved view for everyone in the organisation and stops any scheduled emails. This cannot be undone.` }</Paragraph>
          </Notification>
        ) : (
          <div className={ `flex justify-between items-center pt-2` }>
            <Button color={ `paidniceRedOutline` }
              onClick={ () => setConfirmDelete(true) }>
              { `Delete view` }
            </Button>
            <div className={ `flex gap-2` }>
              <Button color={ `gray` }
                onClick={ handleClose }>
                { `Cancel` }
              </Button>
              { schedulable && (
                <Button onClick={ () => save(schedule) }
                  loading={ saving }
                  disabled={ !filter?.hasExportParams && !filter?.schedule }>
                  { `Save` }
                </Button>
              ) }
            </div>
          </div>
        ) }
      </div>
    </Modal>
  );
}
